import { Column, ColumnDef } from '@tanstack/react-table';
import { SourcingCampaignIndex } from 'api-clients/monolith';
import { ReactNode, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useLocation } from 'react-router-dom';

import InformationCircle from '@/assets/icons/information.svg';
import { CampaignStatusGuideDialog } from '@/components/base/channel-status-pill/campaign-status-guide-dialog';
import { ChannelStatusPill } from '@/components/base/channel-status-pill';
import { ToggleCampaign } from '@/components/pages/channels/toggle-campaign';
import { Button } from '@/components/ui/button';
import { ChannelIcon } from '@/components/ui/channel-icon';
import { SortColumnButton } from '@/components/ui/sort-column-button';
import { Tooltip } from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import { useNavigationPaths } from '@/hooks/use-navigation-paths';
import { usePostHog } from '@/hooks/use-posthog';
import { getCampaignToggleState } from '@/utils/campaign-toggle-state';
import { isFreeChannel, isNonIntegratedChannel } from '@/utils/channel-util';

import { getStatusColumnSize } from './get-status-column-size';
import { campaignEditLink, Menu } from './menu';

function DashCell() {
  return <Typography variant="subtitle1">-</Typography>;
}

function FreeCell() {
  return (
    <Typography variant="subtitle1" className="text-fx-neutral-600 italic">
      <FormattedMessage
        defaultMessage="Free"
        description="Shown in a cost column (spend/budget/CPA/CPH/conversion) for free channels, e.g. Pool or an unpromoted Vonq posting"
      />
    </Typography>
  );
}

function isFreeCampaign(campaign: SourcingCampaignIndex): boolean {
  return isFreeChannel(
    campaign.sourcing_channel,
    // null (unpromoted / unset) and 0 both mean "no paid budget"
    !campaign.budget,
    campaign.contract_id == null,
  );
}

function hasPaidBudget(budget: number | null): budget is number {
  return budget != null && budget > 0;
}

/** + Add is only for non-integrated channels that are still editable. */
function canAddBudget(campaign: SourcingCampaignIndex): boolean {
  const { sourcing_channel: channel } = campaign;
  return (
    isNonIntegratedChannel(
      channel.is_integratable,
      channel.is_publishable,
      !!channel.is_vonq_channel,
    ) &&
    !campaign.is_readonly &&
    campaign.status !== 'completed' &&
    campaign.status !== 'failed'
  );
}

/**
 * Renders a metric value, falling back to a dash for both `null` and `0` —
 * a metric of exactly zero reads the same as "no data" in this table.
 */
function MetricCell({
  value,
  render,
}: {
  value: number | null;
  render: (value: number) => ReactNode;
}) {
  if (!value) return <DashCell />;
  return <Typography variant="subtitle1">{render(value)}</Typography>;
}

export function useCampaignColumns(
  campaigns: SourcingCampaignIndex[] = [],
  defaultMetricsPeriod?: number | null,
): ColumnDef<SourcingCampaignIndex>[] {
  const intl = useIntl();
  const { formatCurrency } = useCurrencySymbol();
  const { allCampaignsPath, dashboardPath } = useNavigationPaths();
  const { search } = useLocation();
  const posthog = usePostHog();
  const statusColumnSize = useMemo(
    () => getStatusColumnSize(campaigns),
    [campaigns],
  );

  const handleSort = (column: Column<SourcingCampaignIndex>) => {
    const nextDirection = column.getIsSorted() === 'asc' ? 'desc' : 'asc';
    posthog.capture('campaigns:sort_click', {
      column_id: column.id,
      direction: nextDirection,
    });
    column.toggleSorting(column.getIsSorted() === 'asc');
  };

  return [
    {
      accessorKey: 'menu',
      size: 20,
      meta: { narrowCell: true },
      header: () => null,
      cell: ({ row }) => (
        <Menu
          campaign={row.original}
          defaultMetricsPeriod={defaultMetricsPeriod}
        />
      ),
    },
    {
      accessorKey: 'toggle',
      size: 44,
      meta: { narrowCell: true },
      header: () => null,
      cell: ({ row }) => {
        const {
          id,
          status,
          sourcing_channel: channel,
          is_readonly: isReadonly,
        } = row.original;

        // Same rules as the Channels page for enablement/tooltip. Always
        // render the toggle here (disabled when not actionable) so the
        // column stays aligned — hiding it leaves an empty gap that looks odd.
        const { isToggleEnabled, showTooltip } = getCampaignToggleState({
          status,
          isReadonly,
          channelSlug: channel.slug,
          isPublishable: channel.is_publishable,
          isVonqChannel: channel.is_vonq_channel,
        });

        return (
          <Tooltip
            content={
              showTooltip &&
              intl.formatMessage({
                defaultMessage:
                  'Pausing or resuming campaigns isn’t available yet for this channel.',
                description:
                  'Tooltip shown when users attempt to pause or resume a campaign',
              })
            }
          >
            <span className="h-4">
              <ToggleCampaign
                sourcingPurchaseId={id}
                reachCampaignId={null}
                status={status}
                channel={channel.slug}
                isDisabled={!isToggleEnabled}
              />
            </span>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: 'opening_name',
      size: 200,
      meta: { narrowCell: true },
      header: ({ column }) => (
        <SortColumnButton
          onSort={() => handleSort(column)}
          sorted={column.getIsSorted()}
        >
          <FormattedMessage
            defaultMessage="Opening name"
            description="Campaigns table column: title of the job opening"
          />
        </SortColumnButton>
      ),
      cell: ({ row }) => (
        <Typography
          variant="body1"
          className="h-auto text-left whitespace-normal"
        >
          {row.original.opening_name}
        </Typography>
      ),
    },
    {
      accessorKey: 'channel',
      size: 108,
      meta: { narrowCell: true },
      header: () => (
        <FormattedMessage
          defaultMessage="Channel"
          description="Campaigns table column: sourcing channel the campaign runs on"
        />
      ),
      cell: ({ row }) => {
        const { sourcing_channel: channel } = row.original;
        if (!channel) return <DashCell />;

        return (
          <ChannelIcon
            variant="large"
            size="sm"
            channelSlug={channel.slug}
            logoRectangleUrl={channel.logo_rectangle}
            channelName={channel.name}
          />
        );
      },
    },
    {
      accessorKey: 'status',
      size: statusColumnSize,
      meta: { narrowCell: true },
      header: () => (
        <div className="flex items-center gap-1">
          <FormattedMessage
            defaultMessage="Status"
            description="Campaigns table column: campaign lifecycle status"
          />
          <CampaignStatusGuideDialog>
            <button type="button" className="cursor-pointer">
              <img src={InformationCircle} alt="" className="h-3 w-3" />
            </button>
          </CampaignStatusGuideDialog>
        </div>
      ),
      cell: ({ row }) => {
        const {
          status,
          time_to_process: timeToProcess,
          sourcing_channel: channel,
        } = row.original;

        const showProcessingTooltip =
          status === 'in_progress' && timeToProcess != null;
        const isNonIntegrated = isNonIntegratedChannel(
          channel.is_integratable,
          channel.is_publishable,
          !!channel.is_vonq_channel,
        );

        return (
          <Tooltip
            content={
              showProcessingTooltip ? (
                <FormattedMessage
                  defaultMessage="Processing time: {hours} hrs"
                  values={{ hours: timeToProcess }}
                  description="Tooltip on Publishing status: expected Vonq activation lead time"
                />
              ) : undefined
            }
          >
            <span>
              <ChannelStatusPill
                status={status}
                isNonIntegrated={isNonIntegrated}
              />
            </span>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: 'budget',
      size: 65,
      header: ({ column }) => (
        <SortColumnButton
          onSort={() => handleSort(column)}
          sorted={column.getIsSorted()}
        >
          <FormattedMessage
            defaultMessage="Budget"
            description="Campaigns table column: total allocated campaign budget"
          />
        </SortColumnButton>
      ),
      cell: ({ row }) => {
        /* Free campaigns */
        if (isFreeCampaign(row.original)) return <FreeCell />;

        /* Vonq contracts */
        if (
          row.original.sourcing_channel.is_vonq_channel &&
          !!row.original.contract_id
        ) {
          return (
            <Typography
              variant="subtitle1"
              className="text-fx-neutral-600 italic"
            >
              <FormattedMessage
                defaultMessage="Managed{br}outside"
                description="Label for Vonq campaigns with a contract managed outside Fountain"
                values={{ br: <br /> }}
              />
            </Typography>
          );
        }

        /* Paid campaigns — 0 is "no budget", never render $0 */
        if (hasPaidBudget(row.original.budget)) {
          const { budget_type: budgetType } = row.original;
          const showBudgetCadence =
            budgetType != null && budgetType !== 'lifetime';

          return (
            <div className="flex flex-col">
              <Typography variant="subtitle1">
                {formatCurrency(row.original.budget)}
              </Typography>
              {showBudgetCadence && (
                <Typography variant="small1" className="text-fx-neutral-400">
                  {budgetType === 'daily' && (
                    <FormattedMessage
                      defaultMessage="Daily"
                      description="Budget cadence under the amount when the campaign budget is a daily cap"
                    />
                  )}
                  {budgetType === 'weekly' && (
                    <FormattedMessage
                      defaultMessage="Weekly"
                      description="Budget cadence under the amount when the campaign budget is a weekly cap"
                    />
                  )}
                  {budgetType === 'monthly' && (
                    <FormattedMessage
                      defaultMessage="Monthly"
                      description="Budget cadence under the amount when the campaign budget is a monthly cap"
                    />
                  )}
                </Typography>
              )}
            </div>
          );
        }

        /* Empty budget on an editable campaign */
        if (canAddBudget(row.original)) {
          const channelSlug = row.original.sourcing_channel?.slug;
          const addBudgetLink = channelSlug
            ? campaignEditLink(
                allCampaignsPath,
                dashboardPath,
                row.original,
                search,
              )
            : undefined;

          return (
            <Button
              variant="link"
              asChild={!!addBudgetLink}
              disabled={!addBudgetLink}
            >
              {addBudgetLink ? (
                <Link to={addBudgetLink.to} state={addBudgetLink.state}>
                  <FormattedMessage
                    defaultMessage="+ Add"
                    description="CTA in Budget column when no budget is set on the campaign"
                  />
                </Link>
              ) : (
                <FormattedMessage
                  defaultMessage="+ Add"
                  description="CTA in Budget column when no budget is set on the campaign"
                />
              )}
            </Button>
          );
        }

        return <DashCell />;
      },
    },
    {
      accessorKey: 'start_date',
      size: 96,
      header: ({ column }) => (
        <SortColumnButton
          onSort={() => handleSort(column)}
          sorted={column.getIsSorted()}
        >
          <FormattedMessage
            defaultMessage="Start date"
            description="Campaigns table column: campaign start date"
          />
        </SortColumnButton>
      ),
      cell: ({ row }) =>
        row.original.start_date == null ? (
          <DashCell />
        ) : (
          <Typography variant="subtitle1">
            {intl.formatDate(row.original.start_date, {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
            })}
          </Typography>
        ),
    },
    {
      accessorKey: 'end_date',
      size: 96,
      header: ({ column }) => (
        <SortColumnButton
          onSort={() => handleSort(column)}
          sorted={column.getIsSorted()}
        >
          <FormattedMessage
            defaultMessage="End date"
            description="Campaigns table column: campaign end date"
          />
        </SortColumnButton>
      ),
      cell: ({ row }) =>
        row.original.end_date == null ? (
          <DashCell />
        ) : (
          <Typography variant="subtitle1">
            {intl.formatDate(row.original.end_date, {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
            })}
          </Typography>
        ),
    },
    {
      accessorKey: 'spend',
      size: 64,
      header: ({ column }) => (
        <SortColumnButton
          onSort={() => handleSort(column)}
          sorted={column.getIsSorted()}
        >
          <FormattedMessage
            defaultMessage="Spend"
            description="Campaigns table column: amount spent so far"
          />
        </SortColumnButton>
      ),
      cell: ({ row }) =>
        isFreeCampaign(row.original) ? (
          <FreeCell />
        ) : (
          <MetricCell value={row.original.spend} render={formatCurrency} />
        ),
    },
    {
      accessorKey: 'applicants',
      size: 98,
      header: ({ column }) => (
        <SortColumnButton
          onSort={() => handleSort(column)}
          sorted={column.getIsSorted()}
        >
          <FormattedMessage
            defaultMessage="Applicants"
            description="Campaigns table column: number of applications received"
          />
        </SortColumnButton>
      ),
      cell: ({ row }) => (
        <MetricCell
          value={row.original.applications}
          render={intl.formatNumber}
        />
      ),
    },
    {
      accessorKey: 'cpa',
      size: 64,
      header: ({ column }) => (
        <SortColumnButton
          onSort={() => handleSort(column)}
          sorted={column.getIsSorted()}
        >
          <FormattedMessage
            defaultMessage="CPA"
            description="Campaigns table column: cost per applicant"
          />
        </SortColumnButton>
      ),
      cell: ({ row }) =>
        isFreeCampaign(row.original) ? (
          <FreeCell />
        ) : (
          <MetricCell value={row.original.cpa} render={formatCurrency} />
        ),
    },
    {
      accessorKey: 'hires',
      size: 64,
      header: ({ column }) => (
        <SortColumnButton
          onSort={() => handleSort(column)}
          sorted={column.getIsSorted()}
        >
          <FormattedMessage
            defaultMessage="Hires"
            description="Campaigns table column: number of hires from this campaign"
          />
        </SortColumnButton>
      ),
      cell: ({ row }) => (
        <MetricCell value={row.original.hires} render={intl.formatNumber} />
      ),
    },
    {
      accessorKey: 'cph',
      size: 64,
      header: ({ column }) => (
        <SortColumnButton
          onSort={() => handleSort(column)}
          sorted={column.getIsSorted()}
        >
          <FormattedMessage
            defaultMessage="CPH"
            description="Campaigns table column: cost per hire"
          />
        </SortColumnButton>
      ),
      cell: ({ row }) =>
        isFreeCampaign(row.original) ? (
          <FreeCell />
        ) : (
          <MetricCell value={row.original.cph} render={formatCurrency} />
        ),
    },
    {
      accessorKey: 'conversion',
      size: 90,
      header: ({ column }) => (
        <SortColumnButton
          onSort={() => handleSort(column)}
          sorted={column.getIsSorted()}
        >
          <FormattedMessage
            defaultMessage="Conversion"
            description="Campaigns table column: application-to-hire conversion rate"
          />
        </SortColumnButton>
      ),
      cell: ({ row }) => (
        <MetricCell
          value={row.original.conversion}
          render={value => `${value}%`}
        />
      ),
    },
  ];
}
